import Head from 'next/head';
import Link from 'next/link';
import { alertPath, alertUrl } from 'utils/alert-url';
import { useState } from 'react';
import sanitizeHtml from 'sanitize-html';
import Typo from 'components/Typo';
import Quote from 'components/Svg/Quote2';
import copyFormatted from 'utils/copyFormattedHTML';
import { excerptFrom } from 'utils/seo-checks';
import {
    firstImage,
    formatDate,
    faqQuestionsBelowHeading,
    hostname,
    isYoutubeShort,
    privateYoutubeSrc,
    storyCopyHtml,
} from 'utils/newsroom';
import { jsonLd } from 'utils/safe-html';
import {
    authorPath,
    bylineAuthor,
    expertPersons,
    storyVideos,
    webUrl,
} from 'utils/news-article-schema';
import { leadImage, leadImageSchema } from 'utils/lead-image';
import { liveSponsor } from 'utils/story-sponsor';
import { storyLinkTransform, storySponsorHosts } from 'utils/story-links';
import ExpertAnswers from './ExpertAnswers';
import StorySponsor from './StorySponsor';
import AuthorBlock, { authorRole } from './AuthorBlock';
import { ResearchCredit } from '../ResearchSponsorBox';
import styles from 'styles/modules/newsAlert/Newsroom.module.scss';
import { useSelector } from 'react-redux';
import { authSelector } from 'store/auth';

const PUBLISHER = {
    '@type': 'Organization',
    name: 'Newspage',
    url: 'https://newspage.media',
    logo: {
        '@type': 'ImageObject',
        url: `${process.env.HOST}/newspage.png`,
    },
};

// Not safeHtml: that one rewrites every outbound rel to "nofollow noopener",
// which would drop the follow an editor chose and the rel="sponsored" paid links need.
export const cleanStoryHtml = (html, sponsorHosts = []) =>
    sanitizeHtml(faqQuestionsBelowHeading(html || ''), {
        transformTags: {
            a: storyLinkTransform(sponsorHosts),
            iframe: (tagName, attribs) => ({
                tagName,
                attribs: {
                    ...attribs,
                    src: privateYoutubeSrc(attribs.src),
                    loading: 'lazy',
                    title: 'Video',
                },
            }),
            div: (tagName, attribs) => ({
                tagName,
                attribs: isYoutubeShort(attribs['data-oembed-url'])
                    ? { ...attribs, class: 'video-short' }
                    : attribs,
            }),
            // CKEditor's dropdown calls <h3> "Heading 2" (its "Heading 1" is
            // <h2>), so the subheadings editors pick sat a level below the H1.
            h3: 'h2',
            h4: 'h3',
        },
        allowedTags: sanitizeHtml.defaults.allowedTags.concat([
            'img',
            'iframe',
            'ins',
        ]),
        allowedAttributes: {
            ...sanitizeHtml.defaults.allowedAttributes,
            a: ['href', 'name', 'target', 'rel'],
            img: ['src', 'srcset', 'sizes', 'alt', 'width', 'height'],
            iframe: [
                'src',
                'width',
                'height',
                'allow',
                'allowfullscreen',
                'loading',
                'title',
            ],
            figure: ['class'],
            '*': ['style'],
        },
        allowedClasses: { div: ['video-short'] },
        allowedStyles: {
            '*': { 'text-align': [/^(left|right|center|justify)$/] },
        },
        allowedIframeHostnames: [
            'www.youtube.com',
            'www.youtube-nocookie.com',
            'player.vimeo.com',
        ],
    });

// A story is published at its alert's address.
export const storyUrl = (slug) => alertUrl(slug);

// NewsArticle structured data, so Google reads the page as a news story whose
// source material (the alert and every response) sits below it. The byline is
// the author; the quoted experts are the people the story mentions.
export const newsArticleJsonLd = (story, alert, responses, videoDates) => {
    const url = storyUrl(alert.slug);
    const lead = leadImage(story);
    const image = firstImage(story.body);
    const mentions = expertPersons(responses, process.env.HOST);
    const description = story.metaDescription || excerptFrom(story.body);
    const videos = storyVideos(story.body, videoDates, {
        headline: story.headline,
        description,
        savedAt: story.updatedAt || story.publishedAt,
    });
    return {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline: story.headline,
        description,
        url,
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        datePublished: story.publishedAt,
        dateModified: story.updatedAt || story.publishedAt,
        author: bylineAuthor(story, process.env.HOST) || {
            '@type': 'Organization',
            name: 'Newspage',
            url: PUBLISHER.url,
        },
        publisher: PUBLISHER,
        image: lead
            ? leadImageSchema(lead)
            : [image || `${process.env.HOST}/newspage.png`],
        isAccessibleForFree: true,
        ...(mentions.length ? { mentions } : {}),
        ...(videos.length ? { video: videos } : {}),
    };
};

// The published story, above the raw alert it was written from.
// `afterStory`: what follows the article itself, before its sources (the sponsorship re-offer).
const Story = ({ story, alert, responses, videoDates, afterStory }) => {
    // The API already keeps http(s) only; checked again because these become hrefs.
    const reportedBy = (alert.asReportedBy ?? []).filter((item) =>
        /^https?:\/\//i.test(item.url)
    );
    const [copied, setCopied] = useState(false);
    const { isAuthenticated } = useSelector(authSelector);
    const sponsor = liveSponsor(story);
    const lead = leadImage(story);
    const author = story.bylineAuthor;
    const authorPhoto = webUrl(author?.photoUrl);
    const role = author && authorRole(author);
    const research =
        alert.researchSponsorship?.status === 'sold'
            ? alert.researchSponsorship
            : null;
    const bodyHtml = cleanStoryHtml(
        story.body,
        storySponsorHosts(story, alert)
    );
    const updated =
        story.updatedAt &&
        formatDate(story.updatedAt) !== formatDate(story.publishedAt);

    const handleCopy = () => {
        copyFormatted(storyCopyHtml(story.headline, bodyHtml, alert.slug));
        setCopied(true);
        setTimeout(() => setCopied(false), 1000);
    };

    return (
        <>
            <Head>
                <script
                    key="news-article-jsonld"
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: jsonLd(
                            newsArticleJsonLd(
                                story,
                                alert,
                                responses,
                                videoDates
                            )
                        ),
                    }}
                />
            </Head>

            <article className={styles.story}>
                {sponsor && <StorySponsor sponsor={sponsor} />}
                <Typo variant={'h1'} className={styles.storyHeadline}>
                    {story.headline}
                </Typo>
                <div className={styles.storyMetaRow}>
                    <p className={styles.storyMeta}>
                        {authorPhoto && (
                            <img
                                className={styles.bylinePhoto}
                                src={authorPhoto}
                                alt=""
                                width={32}
                                height={32}
                            />
                        )}
                        By{' '}
                        {author ? (
                            <Link href={authorPath(author.slug)}>
                                <a>{author.name}</a>
                            </Link>
                        ) : (
                            story.byline || 'Newspage'
                        )}
                        {role && `, ${role}`} ·{' '}
                        <time dateTime={story.publishedAt}>
                            {formatDate(story.publishedAt)}
                        </time>
                        {updated && (
                            <>
                                {' '}
                                · Updated{' '}
                                <time dateTime={story.updatedAt}>
                                    {formatDate(story.updatedAt)}
                                </time>
                            </>
                        )}
                    </p>
                    {/* Members only: logged-out readers (and Google) get no copy button */}
                    {isAuthenticated && (
                        <button
                            type="button"
                            className={styles.copyStory}
                            onClick={copied ? undefined : handleCopy}
                        >
                            <Quote fill="currentColor" />
                            {copied ? 'Copied' : 'Copy story'}
                        </button>
                    )}
                </div>
                {research?.sponsorName && (
                    <ResearchCredit
                        name={research.sponsorName}
                        website={research.sponsorWebsite}
                    />
                )}
                {lead && (
                    <figure className={styles.leadImage}>
                        {/* Not lazy: it is the page's largest picture, above the fold. */}
                        <img
                            src={lead.url}
                            alt={lead.alt}
                            width={lead.width}
                            height={lead.height}
                        />
                        {(lead.caption || lead.credit) && (
                            <figcaption>
                                {[lead.caption, lead.credit]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </figcaption>
                        )}
                    </figure>
                )}
                <div
                    className={styles.storyBody}
                    dangerouslySetInnerHTML={{
                        __html: bodyHtml,
                    }}
                />
                {author && <AuthorBlock author={author} />}
            </article>
            {afterStory}

            {reportedBy.length > 0 && (
                <section className={styles.reportedBy}>
                    <h2>As reported by</h2>
                    <ul>
                        {reportedBy.map((item) => (
                            <li key={item.url}>
                                <a
                                    href={item.url}
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    <strong>
                                        {item.outlet || hostname(item.url)}
                                    </strong>
                                    {item.title && `: ${item.title}`}
                                </a>
                                {item.date && (
                                    <>
                                        {' · '}
                                        <time dateTime={item.date}>
                                            {formatDate(item.date)}
                                        </time>
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            {story.expertAnswers && (
                <ExpertAnswers answers={story.expertAnswers} />
            )}

            {alert.relatedStories?.length > 0 && (
                <section className={styles.related}>
                    <h2>Related stories</h2>
                    <ul>
                        {alert.relatedStories.map((related) => (
                            <li key={related.slug}>
                                <Link href={alertPath(related.slug)}>
                                    <a>{related.headline}</a>
                                </Link>
                                {' · '}
                                <time dateTime={related.publishedAt}>
                                    {formatDate(related.publishedAt)}
                                </time>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <div className={styles.rawDivider}>
                <h2 id="raw-alert">The raw alert</h2>
                <p>
                    The question we put to Newspage&rsquo;s experts, and their
                    answers.
                </p>
            </div>
        </>
    );
};

export default Story;
